import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { limit, QueryConstraint, startAfter, where } from 'firebase/firestore';
import { firestoreQuery, firestorGetDoc } from '../firebase';
import { AppThunk } from '../store';
import { NftData } from '../types/nft';
import equal from 'deep-equal';
import merge from 'deepmerge';
import { padNumber } from '../utils/pad';

interface NftsState {
  loading: boolean;
  loadingMore: boolean;
  list: NftData[];
  cache: {
    [ tokenId: number ]: NftData;
  },
  cursor: any;
}

const initialState: NftsState = {
  loading: false,
  loadingMore: false,
  list: [],
  cache: {},
  cursor: null
}


const service = createSlice({
  name: 'nfts',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>): NftsState {
      return { ...state, loading: action.payload, }
    },
    setLoadingMore(state, action: PayloadAction<boolean>): NftsState {
      return { ...state, loadingMore: action.payload, }
    },
    setList(state, action: PayloadAction<NftData[]>): NftsState {
      const cache = { ...state.cache };
      for (const nft of action.payload) {
        cache[ nft.tokenId ] = nft;
      }
      return { ...state, list: [ ...action.payload ], cache, };
    },
    appendList(state, action: PayloadAction<NftData[]>): NftsState {
      const cache = { ...state.cache };
      for (const nft of action.payload) {
        cache[ nft.tokenId ] = nft;
      }
      return {
        ...state,
        list: [
          ...state.list,
          ...action.payload
        ],
        cache,
      };
    },
    setCache(state, action: PayloadAction<NftData>): NftsState {
      const cache = {
        ...state.cache,
        [ action.payload.tokenId ]: action.payload,
      };

      return {
        ...state,
        cache,
      };
    },
    setCursor(state, action: PayloadAction<any>): NftsState {
      return {
        ...state,
        cursor: action.payload,
      };
    },
  }
});


let prevFilters: any;

const load = (loadingMore: boolean): AppThunk => async (dispatch, getState) => {
  try {
    const cursor = getState().nfts.cursor;
    const filters = getState().filter;

    if (equal(prevFilters, filters) && !loadingMore) {
      return;
    }

    prevFilters = merge({}, filters);

    if (loadingMore && !cursor) {
      return;
    }

    if (loadingMore) {
      dispatch(service.actions.setLoadingMore(true));
    }
    else {
      dispatch(service.actions.setLoading(true));
    }

    const constrains: QueryConstraint[] = [];

    if (filters.rarity !== 'All') {
      constrains.push(
        where('rarity', '==', filters.rarity)
      );
    }

    for (const trait in filters.traits) {
      if (filters.traits[ trait ] !== 'All')
        constrains.push(
          where(`attributes.${trait}`, '==', filters.traits[ trait ])
        )
    }



    if (loadingMore && cursor) {
      constrains.push(startAfter(cursor));
    }

    constrains.push(limit(50));

    const result = await firestoreQuery<NftData>('nfts', constrains)

    if (loadingMore) {
      dispatch(service.actions.appendList(result.list));
    }
    else {
      dispatch(service.actions.setList(result.list));
    }
    dispatch(service.actions.setCursor(result.cursor));
  }
  catch (e) {
    console.error(e);
  }
  finally {
    if (loadingMore) {
      dispatch(service.actions.setLoadingMore(false));
    }
    else {
      dispatch(service.actions.setLoading(false));
    }
  }
}

const loadById = (tokenId: number): AppThunk => async (dispatch, getState) => {
  console.log('load by id', tokenId);
  try {
    const data = await firestorGetDoc<NftData>('nfts', [ padNumber(tokenId, 4) ]);
    if (data) {
      dispatch(service.actions.setCache(data));
    }
  }
  catch (e) {
    console.error(e);
  }
}





export const actions = {
  load,
  loadById,
  setList: service.actions.setList,
}

export default service.reducer;